/**
 * NotificationDropdownPanel — pop-over panel content rendered under
 * the bell icon.
 *
 * Displays:
 *   - Header: "Notifications" + "Mark all read" affordance (disabled
 *     when there's nothing unread or a mark-all is in flight)
 *   - Body: NotificationRow list (compact mode), or empty state
 *   - Footer: "See all" link → /notifications
 *
 * All action buttons carry data-testids for the e1_tester agent.
 */
import { Link } from "react-router-dom";
import { CheckCheck, X, Loader2 } from "lucide-react";
import NotificationRow from "./NotificationRow";
import { EMPTY_STATE_COPY } from "./utils";

export default function NotificationDropdownPanel({
  items,
  loading,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onMarkAllRefreshError,
  onDismissMarkAllError,
  markAllInFlight,
  onNavigateClose,
  onClose,
}) {
  const hasItems = items && items.length > 0;
  return (
    <div
      data-testid="app-nav-notifications-dropdown"
      className="absolute right-0 mt-2 w-96 max-h-[520px] rounded-v2-xl overflow-hidden bg-v2-surface border border-v2-border shadow-v2-modal flex flex-col"
    >
      <header
        data-testid="app-nav-notifications-header"
        className="flex items-center justify-between px-4 py-3 border-b border-v2-border"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-v2-body-sm font-semibold text-v2-text">
            Notifications
          </h3>
          {unreadCount > 0 && (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{
                background: "rgba(167,139,250,0.14)",
                border: "1px solid rgba(167,139,250,0.42)",
                color: "rgb(167,139,250)",
              }}
            >
              {unreadCount} new
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onMarkAllRead}
          disabled={unreadCount === 0 || markAllInFlight}
          data-testid="app-nav-notifications-mark-all-read"
          className="inline-flex items-center gap-1 text-v2-caption text-v2-textMuted hover:text-v2-text disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {markAllInFlight ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <CheckCheck className="w-3 h-3" />
          )}
          Mark all read
        </button>
      </header>

      {onMarkAllRefreshError && (
        <div
          data-testid="app-nav-notifications-mark-all-error"
          role="alert"
          className="flex items-start gap-2 px-4 py-2 text-[11px] text-v2-text"
          style={{
            background: "rgba(239,68,68,0.06)",
            borderBottom: "1px solid rgba(239,68,68,0.28)",
          }}
        >
          <span className="flex-1 leading-relaxed">
            {onMarkAllRefreshError}
          </span>
          <button
            type="button"
            onClick={onDismissMarkAllError}
            data-testid="app-nav-notifications-mark-all-error-dismiss"
            aria-label="Dismiss error"
            className="flex-shrink-0 text-v2-textMuted hover:text-v2-text"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loading && !hasItems ? (
          <div
            data-testid="app-nav-notifications-loading"
            className="flex items-center gap-2 px-4 py-6 text-v2-caption text-v2-textMuted"
          >
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading notifications…
          </div>
        ) : hasItems ? (
          <ul data-testid="app-nav-notifications-list">
            {items.map((n) => (
              <li key={n.id}>
                <NotificationRow
                  notification={n}
                  mode="compact"
                  onMarkRead={onMarkRead}
                  onNavigateClose={onNavigateClose}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div
            data-testid="app-nav-notifications-empty"
            className="px-4 py-8 text-center"
          >
            <p className="text-v2-caption text-v2-textMuted leading-relaxed">
              {EMPTY_STATE_COPY}
            </p>
          </div>
        )}
      </div>

      <footer className="border-t border-v2-border">
        <Link
          to="/notifications"
          onClick={onClose}
          data-testid="app-nav-notifications-see-all"
          className="block px-4 py-3 text-center text-v2-caption font-semibold text-v2-accent hover:bg-v2-surfaceSubtle transition-colors"
        >
          See all notifications
        </Link>
      </footer>
    </div>
  );
}
